import React, { useEffect, useRef, useState } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import { axiosInstance } from '../Config'
import Pagination, { PerPageSelect } from '../../components/Pagination'

const avatarColors = ['#0CACC2', '#A44CD7', '#F55145', '#F9BE22', '#8A3BBD', '#19C37D']
const getInitials  = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
const labelStyle   = { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }

const UsersIndex = () => {
    const [users, setUsers]               = useState([])
    const [loading, setLoading]           = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [searchQuery, setSearchQuery]   = useState('')
    const [selectedUser, setSelectedUser] = useState(null)
    const [page, setPage]                 = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [totalItems, setTotalItems]     = useState(0)
    const [totalPages, setTotalPages]     = useState(1)

    const searchDebounce = useRef(null)

    useEffect(() => {
        document.title = 'Users | Mindora'
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        fetchData(page, itemsPerPage, searchQuery)
    }, [page, itemsPerPage])

    useEffect(() => {
        clearTimeout(searchDebounce.current)
        searchDebounce.current = setTimeout(() => {
            setPage(1)
            fetchData(1, itemsPerPage, searchQuery)
        }, 400)
        return () => clearTimeout(searchDebounce.current)
    }, [searchQuery])

    const fetchData = async (pg, lmt, search) => {
        setLoading(true)
        try {
            const params = new URLSearchParams({
                page:  pg,
                limit: lmt,
                ...(search.trim() && { search: search.trim() }),
            })
            const res  = await axiosInstance.get(`admin/kids?${params}`)
            const body = res.data
            setUsers(body.data ?? [])
            setTotalItems(body.pagination?.total ?? 0)
            setTotalPages(body.pagination?.totalPages ?? 1)
        } catch {
            swal('', 'Failed to load users.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const openDetail = async (userKey) => {
        setModalLoading(true)
        try {
            const res = await axiosInstance.get(`admin/kids/${userKey}`)
            setSelectedUser(res.data?.data ?? res.data)
        } catch {
            swal('', 'Failed to load user details.', 'error')
        } finally {
            setModalLoading(false)
        }
    }

    const uniqueParents = new Set(users.map(u => u.parent?.email).filter(Boolean)).size
    const activeCount   = users.filter(u => u.status === true).length

    return (
        <>
            {(loading || modalLoading) && <Loader />}
            <ContainerDashboard title="Users" description="All child accounts with their parent details">
                <section className="ps-dashboards p-4">

                    {/* Stats Row */}
                    <div className="row g-3 mb-4">
                        {[
                            { label: 'Total Users',    value: totalItems,      icon: 'bi-people',       color: '#0CACC2', bg: 'rgba(12,172,194,0.12)' },
                            { label: 'Parent Accounts', value: uniqueParents,  icon: 'bi-person-badge', color: '#A44CD7', bg: 'rgba(164,76,215,0.12)' },
                            { label: 'Active Users',   value: activeCount,     icon: 'bi-person-check', color: '#19C37D', bg: 'rgba(25,195,125,0.12)' },
                        ].map(s => (
                            <div className="col-md-4" key={s.label}>
                                <div className="rounded-4 p-3 d-flex align-items-center gap-3"
                                    style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 48, height: 48, background: s.bg }}>
                                        <i className={`bi ${s.icon}`} style={{ fontSize: 22, color: s.color }}></i>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={labelStyle}>{s.label}</p>
                                        <h4 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{s.value}</h4>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Search + title */}
                    <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-wrap">
                        <div className="d-flex align-items-center">
                            <div className="ps-form--search-simple">
                                <input
                                    className="form-control bg-white"
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search by name, parent or email..."
                                />
                                <button type="button">
                                    <i className="icon icon-magnifier"></i>
                                </button>
                            </div>
                            <PerPageSelect itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} setPage={setPage} />
                        </div>
                        <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 16 }}>
                            All Users
                            <span className="ms-2 badge rounded-pill"
                                style={{ background: 'rgba(12,172,194,0.12)', color: '#0CACC2', fontSize: 12 }}>
                                {totalItems}
                            </span>
                            {searchQuery && (
                                <span className="ms-2 text-muted fw-normal" style={{ fontSize: 13 }}>
                                    — results for "{searchQuery}"
                                </span>
                            )}
                        </h6>
                    </div>

                    {/* Table */}
                    <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12 }}>
                        <table className="jean-table table" style={{ minWidth: 800 }}>
                            <tbody>
                                {users.map((user, index) => {
                                    return (
                                        <tr key={user.userKey}>
                                            <td style={{ width: 60 }}>
                                                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{ width: 46, height: 46, background: avatarColors[index % avatarColors.length], color: '#fff', fontWeight: 700, fontSize: 15 }}>
                                                    {getInitials(user.username)}
                                                </div>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">User</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{user.username}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>Age {user.age}</p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Parent</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{user.parent?.name || '—'}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>
                                                    <i className="bi bi-envelope me-1"></i>{user.parent?.email}
                                                </p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Phone</p>
                                                <p className="mb-0" style={{ fontSize: 14, color: '#1a1a2e' }}>{user.phone || user.parent?.phone || '—'}</p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Joined</p>
                                                <p className="mb-0" style={{ fontSize: 14, color: '#1a1a2e' }}>
                                                    <i className="bi bi-calendar3 me-1" style={{ color: '#bbb' }}></i>
                                                    {user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : '—'}
                                                </p>
                                            </td>
                                            <td className="pe-3">
                                                <div className="d-flex justify-content-end">
                                                    <button type="button" className="btn fw-semibold"
                                                        style={{ background: 'linear-gradient(135deg, #0CACC2, #A44CD7)', color: '#fff', borderRadius: 8, padding: '6px 16px', fontSize: 13, border: 'none', whiteSpace: 'nowrap' }}
                                                        onClick={() => openDetail(user.userKey)}>
                                                        <i className="bi bi-bar-chart-line me-1"></i>View More
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                                {users.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="text-center py-5" style={{ color: '#aaa' }}>
                                                <i className="bi bi-people" style={{ fontSize: 52, opacity: 0.3 }}></i>
                                                <p className="mt-3 mb-0">
                                                    {searchQuery ? `No users match "${searchQuery}".` : 'No users found.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        page={page} totalPages={totalPages}
                        setPage={setPage} totalItems={totalItems}
                        showingCount={users.length}
                    />

                </section>
            </ContainerDashboard>

            {selectedUser && (
                <UserDetailModal
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                />
            )}
        </>
    )
}

const UserDetailModal = ({ user, onClose }) => {
    const isActive = user.status === true
    const ao = user.activityOverview ?? {}

    const stats = [
        { label: 'Tokens Used',      value: (ao.tokensUsed ?? 0).toLocaleString(), icon: 'bi bi-cpu',       color: '#0CACC2', bg: 'rgba(12,172,194,0.12)' },
        { label: 'Images Generated', value: ao.imagesGenerated ?? 0,               icon: 'bi bi-image',     color: '#A44CD7', bg: 'rgba(164,76,215,0.12)' },
        { label: 'Videos Generated', value: ao.videosGenerated ?? 0,               icon: 'bi bi-film',      color: '#F55145', bg: 'rgba(245,81,69,0.12)' },
        { label: 'Total Chats',      value: ao.totalChats ?? 0,                    icon: 'bi bi-chat-dots', color: '#F9BE22', bg: 'rgba(249,190,34,0.12)' },
    ]

    const detailItems = [
        { label: 'Email',  value: user.parent?.email, icon: 'bi bi-envelope', color: '#0CACC2', show: !!user.parent?.email },
        { label: 'Phone',  value: user.phone || user.parent?.phone, icon: 'bi bi-telephone', color: '#A44CD7', show: !!(user.phone || user.parent?.phone) },
        { label: 'Joined', value: user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : null, icon: 'bi bi-calendar3', color: '#F55145', show: !!user.joinedDate },
        { label: 'Status', value: isActive ? 'Active' : 'Inactive', icon: isActive ? 'bi bi-check-circle' : 'bi bi-x-circle', color: isActive ? '#19C37D' : '#F55145', show: true },
    ]

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={e => { if (e.target === e.currentTarget) onClose() }}
        >
            <style>{`
                @keyframes userDetailIn {
                    from { transform: translateY(-24px) scale(0.96); opacity: 0; }
                    to   { transform: translateY(0) scale(1); opacity: 1; }
                }
                .stat-item { transition: all 0.2s ease; border-radius: 12px; cursor: default; }
                .stat-item:hover { background: #f8f9fe; transform: translateX(4px); }
                .detail-item { transition: all 0.2s ease; }
                .detail-item:hover { transform: translateX(4px); }
            `}</style>

            <div style={{ width: '100%', maxWidth: 680, animation: 'userDetailIn 0.25s ease' }}>
                <div className="rounded-4 overflow-hidden" style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)' }}>

                    {/* Header with decorative elements */}
                    <div className="position-relative overflow-hidden px-4 py-4"
                        style={{ background: 'linear-gradient(135deg, #0CACC2 0%, #7B4FBF 50%, #A44CD7 100%)' }}>
                        <div style={{ position: 'absolute', top: '-30px', right: '-20px', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }}></div>
                        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }}></div>
                        <div className="d-flex align-items-center gap-3 position-relative">
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', color: '#fff', fontWeight: 700, fontSize: 18, boxShadow: '0 4px 15px rgba(0,0,0,0.15)', border: '2px solid rgba(255,255,255,0.3)' }}>
                                {getInitials(user.username)}
                            </div>
                            <div className="flex-grow-1">
                                <p className="fw-bold text-white mb-0" style={{ fontSize: 17, letterSpacing: '0.3px' }}>{user.username}</p>
                                <p className="mb-0" style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                                    <i className="bi bi-cake me-1"></i>Age {user.age}
                                    {user.parent?.name && <> &nbsp;·&nbsp; <i className="bi bi-person me-1"></i>{user.parent.name}</>}
                                </p>
                            </div>
                            <button onClick={onClose}
                                style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: 34, height: 34, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s ease' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)' }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)' }}>
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Two-column body */}
                    <div className="row g-0">
                        {/* Left: Activity Overview */}
                        <div className="col-6 p-4" style={{ borderRight: '1px solid #f0f0f0' }}>
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <div style={{ width: 3, height: 18, background: 'linear-gradient(180deg, #0CACC2, #A44CD7)', borderRadius: 2 }}></div>
                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: '#1a1a2e', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                                    Activity Overview
                                </p>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                {stats.map((s, i) => (
                                    <div key={i} className="stat-item d-flex align-items-center gap-3 p-3">
                                        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 42, height: 42, background: s.bg }}>
                                            <i className={s.icon} style={{ fontSize: 18, color: s.color }}></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <p className="mb-0" style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                {s.label}
                                            </p>
                                            <p className="mb-0 fw-bold" style={{ fontSize: 14, color: '#1a1a2e', marginTop: 1 }}>{s.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: Details */}
                        <div className="col-6 p-4" style={{ background: '#fafbfc' }}>
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <div style={{ width: 3, height: 18, background: 'linear-gradient(180deg, #A44CD7, #0CACC2)', borderRadius: 2 }}></div>
                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: '#1a1a2e', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                                    Details
                                </p>
                            </div>
                            <div className="d-flex flex-column gap-3">
                                {detailItems.filter(d => d.show).map((d, i) => (
                                    <div key={i} className="detail-item d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                                        <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 36, height: 36, background: `${d.color}15` }}>
                                            <i className={d.icon} style={{ fontSize: 15, color: d.color }}></i>
                                        </div>
                                        <div>
                                            <p className="mb-0" style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                {d.label}
                                            </p>
                                            <p className="mb-0 fw-semibold" style={{ fontSize: 13, color: '#1a1a2e', marginTop: 1 }}>{d.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default UsersIndex
